// T062: Card Loader Service
// Loads card definitions from generated JSON files

import type { CardDefinition, Faction } from '../types';
import { FactionEffectMapper } from './FactionEffectMapper';
import { CardDataImporter, type CardDataRow } from './CardDataImporter';
import { EffectTextParser } from '../core/effects/EffectTextParser';

// Import faction data
import neutralSpellData from '../data/cards/neutral_spell.json';
import neutralUnitData from '../data/cards/neutral_unit.json';
import powerBeastData from '../data/cards/power_beast.json';
import carbonData from '../data/cards/carbon.json';
import fairyData from '../data/cards/fairy.json';
import artworkData from '../data/cards/artwork.json';
import travelerData from '../data/cards/traveler.json';
import savageData from '../data/cards/savage.json';
import orcData from '../data/cards/orc.json';
import catData from '../data/cards/cat.json';
import candyData from '../data/cards/candy.json';
import robotData from '../data/cards/robot.json';
import skeletonData from '../data/cards/skeleton.json';
import steamData from '../data/cards/steam.json';
import goatData from '../data/cards/goat.json';
import monsterData from '../data/cards/monster.json';
import virtualData from '../data/cards/virtual.json';
import heroData from '../data/cards/hero.json';
import islandData from '../data/cards/island.json';
import circusData from '../data/cards/circus.json';
import factionsData from '../data/cards/factions.json';

interface FactionData {
  faction: string;
  factionCode?: string;
  factionNameZh?: string;
  factionNameEn?: string;
  stats?: {
    total: number;
    gameReady: number;
    withEffect?: number;
    missingImage?: number;
  };
  cards: RawCardData[];
}

interface RawCardData {
  id: string;
  code: string;
  name: string;
  nameZh: string;
  faction: string;
  type: 'unit' | 'spell' | 'field';
  rarity: 'N' | 'R' | 'SR' | 'SSR' | 'SSSR';
  imagePath?: string;
  variants?: string[];
  // New fields from official data
  atk?: number | null;
  score?: number | null;
  sacrificeRequired?: number | null;
  effectText?: string;
  cost?: number | null;
  spellType?: string;
  isField?: boolean;
  gameReady?: boolean;
  imageVerified?: boolean;
  dbId?: string;
}

interface FactionInfo {
  id: string;
  code: string;
  nameZh: string;
  nameEn: string;
  cardCount: number;
}

// Map faction IDs to their data
const FACTION_DATA_MAP: Record<string, FactionData> = {
  neutral_spell: neutralSpellData as FactionData,
  neutral_unit: neutralUnitData as FactionData,
  power_beast: powerBeastData as FactionData,
  carbon: carbonData as FactionData,
  fairy: fairyData as FactionData,
  artwork: artworkData as FactionData,
  traveler: travelerData as FactionData,
  savage: savageData as FactionData,
  orc: orcData as FactionData,
  cat: catData as FactionData,
  candy: candyData as FactionData,
  robot: robotData as FactionData,
  skeleton: skeletonData as FactionData,
  steam: steamData as FactionData,
  goat: goatData as FactionData,
  monster: monsterData as FactionData,
  virtual: virtualData as FactionData,
  hero: heroData as FactionData,
  island: islandData as FactionData,
  circus: circusData as FactionData,
};

// Cache for loaded definitions
let cardDefinitionsCache: Map<string, CardDefinition> | null = null;
let factionCardsCache: Map<Faction, CardDefinition[]> | null = null;
let customCardData: CardDataRow[] | null = null;

/**
 * Get default stats based on rarity.
 * Units have ATK and score; spells/fields have cost.
 */
function getDefaultStats(rarity: string, type: string): { atk: number; score: number; cost: number } {
  const statsMap: Record<string, { atk: number; score: number; cost: number }> = {
    N: { atk: 1, score: 1, cost: 1 },
    R: { atk: 2, score: 2, cost: 2 },
    SR: { atk: 3, score: 3, cost: 3 },
    SSR: { atk: 4, score: 4, cost: 4 },
    SSSR: { atk: 5, score: 5, cost: 5 },
  };

  const base = statsMap[rarity] ?? { atk: 1, score: 1, cost: 1 };

  // Spells don't have ATK
  if (type === 'spell') {
    return { atk: 0, score: 0, cost: base.cost };
  }

  return base;
}

/**
 * Convert raw card data to CardDefinition.
 */
function toCardDefinition(raw: RawCardData): CardDefinition {
  const defaultStats = getDefaultStats(raw.rarity, raw.type);

  // Get effects based on faction and rarity (fallback)
  let effects = FactionEffectMapper.getEffectsForCard(
    raw.id,
    raw.faction as Faction,
    raw.rarity
  );

  // Parse effectText to get effectIds if available
  if (raw.effectText) {
    const parsedEffects = EffectTextParser.getEffectIds(raw.effectText);
    if (parsedEffects.length > 0) {
      // Combine parsed effects with faction-based effects (dedup)
      const allEffects = new Set([...effects, ...parsedEffects]);
      effects = Array.from(allEffects);
    }
  }

  // Use official stats if available, otherwise use defaults
  const atk = raw.atk !== undefined && raw.atk !== null ? raw.atk : defaultStats.atk;
  const score = raw.score !== undefined && raw.score !== null ? raw.score : defaultStats.score;
  const cost = raw.cost !== undefined && raw.cost !== null ? raw.cost : defaultStats.cost;

  return {
    id: raw.id,
    code: raw.code,
    name: raw.name,
    nameZh: raw.nameZh,
    type: raw.type,
    faction: raw.faction as Faction,
    rarity: raw.rarity,
    imagePath: raw.imagePath,
    variants: raw.variants ?? [],
    // Stats: prefer official data, fallback to defaults
    atk,
    score,
    cost,
    sacrificeRequired: raw.sacrificeRequired ?? undefined,
    // Effect data
    effectIds: effects,
    effectText: raw.effectText,
    // Spell properties
    spellType: raw.spellType,
    isField: raw.isField,
    // Game readiness
    gameReady: raw.gameReady,
    imageVerified: raw.imageVerified,
  };
}

/**
 * Load all card definitions into a Map.
 */
export function loadAllCards(): Map<string, CardDefinition> {
  if (cardDefinitionsCache) {
    return cardDefinitionsCache;
  }

  let definitions = new Map<string, CardDefinition>();

  for (const factionData of Object.values(FACTION_DATA_MAP)) {
    for (const rawCard of factionData.cards) {
      const def = toCardDefinition(rawCard);
      definitions.set(def.id, def);
    }
  }

  // Apply custom CSV data if available
  if (customCardData && customCardData.length > 0) {
    definitions = CardDataImporter.applyCSVData(definitions, customCardData);
  }

  cardDefinitionsCache = definitions;
  return definitions;
}

/**
 * Import card data from CSV text.
 * This will override default stats with CSV values.
 */
export function importFromCSV(csvText: string): { imported: number; errors: string[] } {
  const rows = CardDataImporter.parseCSV(csvText);
  const errors: string[] = [];

  // Validate effects
  for (const row of rows) {
    if (row.effects) {
      const effectList = row.effects.split(/[,;]/).map((e) => e.trim()).filter(Boolean);
      const { invalid } = CardDataImporter.validateEffects(effectList);
      if (invalid.length > 0) {
        errors.push(`Card ${row.id}: Invalid effects: ${invalid.join(', ')}`);
      }
    }
  }

  // Store custom data and clear cache
  customCardData = rows;
  clearCardCache();

  return { imported: rows.length, errors };
}

/**
 * Import card data from File object (browser).
 */
export async function importFromFile(file: File): Promise<{ imported: number; errors: string[] }> {
  const rows = await CardDataImporter.loadCSVFile(file);
  const errors: string[] = [];

  for (const row of rows) {
    if (row.effects) {
      const effectList = row.effects.split(/[,;]/).map((e) => e.trim()).filter(Boolean);
      const { invalid } = CardDataImporter.validateEffects(effectList);
      if (invalid.length > 0) {
        errors.push(`Card ${row.id}: Invalid effects: ${invalid.join(', ')}`);
      }
    }
  }

  customCardData = rows;
  clearCardCache();

  return { imported: rows.length, errors };
}

/**
 * Export current card data as CSV.
 */
export function exportToCSV(): string {
  const definitions = loadAllCards();
  return CardDataImporter.generateCSVTemplate(definitions);
}

/**
 * Download CSV template with all cards.
 */
export function downloadCSVTemplate(): void {
  const definitions = loadAllCards();
  CardDataImporter.downloadCSVTemplate(definitions);
}

/**
 * Load cards for a specific faction.
 */
export function loadFactionCards(faction: Faction): CardDefinition[] {
  const cached = factionCardsCache?.get(faction);
  if (cached) {
    return cached;
  }

  if (!factionCardsCache) {
    factionCardsCache = new Map();
  }

  const factionData = FACTION_DATA_MAP[faction];
  const cards = factionData.cards.map(toCardDefinition);
  factionCardsCache.set(faction, cards);
  return cards;
}

/**
 * Get a single card definition by ID.
 */
export function getCardDefinition(cardId: string): CardDefinition | undefined {
  const allCards = loadAllCards();
  return allCards.get(cardId);
}

/**
 * Get all available factions.
 */
export function getAllFactions(): FactionInfo[] {
  return (factionsData as { factions: FactionInfo[] }).factions;
}

/**
 * Get playable factions (excluding neutral categories).
 */
export function getPlayableFactions(): FactionInfo[] {
  return getAllFactions().filter(
    (f) => f.id !== 'neutral_spell' && f.id !== 'neutral_unit'
  );
}

/**
 * Get faction info by ID.
 */
export function getFactionInfo(factionId: Faction): FactionInfo | undefined {
  return getAllFactions().find((f) => f.id === factionId);
}

/**
 * Search cards by name (Chinese or English).
 */
export function searchCardsByName(query: string): CardDefinition[] {
  const allCards = loadAllCards();
  const lowerQuery = query.toLowerCase();

  return Array.from(allCards.values()).filter(
    (card) =>
      card.name.toLowerCase().includes(lowerQuery) ||
      card.nameZh.includes(query)
  );
}

/**
 * Filter cards by type.
 */
export function getCardsByType(
  type: 'unit' | 'spell' | 'field',
  faction?: Faction
): CardDefinition[] {
  const cards = faction ? loadFactionCards(faction) : Array.from(loadAllCards().values());
  return cards.filter((card) => card.type === type);
}

/**
 * Filter cards by rarity.
 */
export function getCardsByRarity(
  rarity: 'N' | 'R' | 'SR' | 'SSR' | 'SSSR',
  faction?: Faction
): CardDefinition[] {
  const cards = faction ? loadFactionCards(faction) : Array.from(loadAllCards().values());
  return cards.filter((card) => card.rarity === rarity);
}

/**
 * Get neutral cards (spells and units combined).
 */
export function getNeutralCards(): CardDefinition[] {
  return [
    ...loadFactionCards('neutral_spell'),
    ...loadFactionCards('neutral_unit'),
  ];
}

/**
 * Clear all caches (useful for hot reloading).
 */
export function clearCardCache(): void {
  cardDefinitionsCache = null;
  factionCardsCache = null;
}

/**
 * Get total card count.
 */
export function getTotalCardCount(): number {
  return loadAllCards().size;
}

// Export as object for backwards compatibility
export const CardLoader = {
  loadAllCards,
  loadFactionCards,
  getCardDefinition,
  getAllFactions,
  getPlayableFactions,
  getFactionInfo,
  searchCardsByName,
  getCardsByType,
  getCardsByRarity,
  getNeutralCards,
  clearCardCache,
  getTotalCardCount,
  // CSV import/export
  importFromCSV,
  importFromFile,
  exportToCSV,
  downloadCSVTemplate,
};
